// DEPLOY-PROBE-bump

/**
 * AutocompleteInput Component
 * 
 * A reusable autocomplete input component for searching and selecting construction workers.
 * Provides debounced search functionality with keyboard navigation support.
 * 
 * Features:
 * - Debounced search (300ms delay) to reduce API calls
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Suggestion filtering and sorting (exact matches first)
 * - Loading state indication
 * - Click-to-select functionality
 * 
 * @component
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fileUploadService } from '../../../../services/FileUploadService';
import './AutocompleteInput.css';
import { compactContainsQuery, compactForMatch } from '../../../../utils/personNameMatch';

/**
 * Props interface for AutocompleteInput component.
 */
interface AutocompleteInputProps {
  value: string;                      // Current input value
  onChange: (value: string) => void;  // Callback when input value changes
  placeholder?: string;               // Placeholder text (default: "従業員名を入力")
  disabled?: boolean;                 // Whether input is disabled (default: false)
  className?: string;                 // Additional CSS classes
  id?: string;                        // Input element ID (default: "emp-name")
  onSelect?: (value: string) => void; // Callback when a suggestion is selected
}

/**
 * AutocompleteInput Component
 * 
 * Provides autocomplete functionality for construction worker name input.
 * Fetches suggestions from FileUploadService and filters them based on user input.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  placeholder = "従業員名を入力",
  disabled = false,
  className = "",
  id = "emp-name",
  onSelect
}) => {
  /**
   * State management:
   * - suggestions: Array of suggestion strings to display
   * - showSuggestions: Whether to show the suggestions dropdown
   * - isLoading: Whether a search is currently in progress
   * - selectedIndex: Index of currently highlighted suggestion (-1 for none)
   */
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  
  /**
   * Refs for DOM element access:
   * - inputRef: Reference to the input element
   * - suggestionsRef: Reference to the suggestions dropdown container
   * - debounceTimeoutRef: Reference to the debounce timeout for cleanup
   */
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Debounced search function for fetching employee suggestions.
   * 
   * Fetches all available construction workers and filters them based on the search term.
   * Results are sorted by relevance (exact matches first, then starts-with matches, then alphabetical).
   * 
   * @param searchTerm - Search term to filter employees by
   * 
   * @private
   */
  const debouncedSearch = useCallback(async (searchTerm: string) => {
    // Don't search if search term is too short
    if (searchTerm.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      /**
       * Get all available construction workers from the service.
       * Filters and sorts them client-side for better performance.
       */
      const allEmployees = await fileUploadService.getConstructionAvailableEmployees();
      
      /**
       * Filter employees that contain the search term (case-insensitive).
       * Uses includes() to match anywhere in the name, not just the beginning.
       */
      const filteredEmployees = allEmployees.filter(employee => 
        compactContainsQuery(employee, searchTerm)
      );
      
      /**
       * Sort by relevance (on compacted strings):
       * 1. Exact matches (highest priority)
       * 2. Starts with search term (second priority)
       * 3. Alphabetical order (lowest priority)
       */
      const sortedEmployees = filteredEmployees.sort((a, b) => {
        const aLower = compactForMatch(a).toLowerCase();
        const bLower = compactForMatch(b).toLowerCase();
        const searchLower = compactForMatch(searchTerm).toLowerCase();
        
        // Exact match gets highest priority
        if (aLower === searchLower) return -1;
        if (bLower === searchLower) return 1;
        
        // Starts with search term gets second priority
        if (aLower.startsWith(searchLower) && !bLower.startsWith(searchLower)) return -1;
        if (bLower.startsWith(searchLower) && !aLower.startsWith(searchLower)) return 1;
        
        // Alphabetical order for everything else
        return a.localeCompare(b);
      });
      
      // Limit to 10 suggestions for better UX
      setSuggestions(sortedEmployees.slice(0, 10));
      setShowSuggestions(sortedEmployees.length > 0);
    } catch (error) {
      console.error('Error fetching employee suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Handles input value changes with debouncing.
   * 
   * Updates the input value immediately and triggers a debounced search
   * after 300ms of no typing to reduce API calls.
   * 
   * @param e - Change event from input element
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Clear previous timeout if user is still typing
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout for debounced search (300ms delay)
    debounceTimeoutRef.current = setTimeout(() => {
      debouncedSearch(newValue);
    }, 300);
  };

  /**
   * Handles suggestion selection from dropdown.
   * 
   * Updates the input value, hides suggestions, and calls onSelect callback.
   * 
   * @param suggestion - Selected suggestion string
   */
  const handleSuggestionSelect = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    if (onSelect) {
      onSelect(suggestion);
    }
  };

  /**
   * Handles keyboard navigation within suggestions dropdown.
   * 
   * Supports:
   * - ArrowDown: Move to next suggestion
   * - ArrowUp: Move to previous suggestion (or -1 if at top)
   * - Enter: Select highlighted suggestion
   * - Escape: Close suggestions dropdown
   * 
   * @param e - Keyboard event from input element
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only handle keyboard navigation if suggestions are visible
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Move to next suggestion, or stay at last if already at end
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        // Move to previous suggestion, or -1 if at first
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        // Select highlighted suggestion if valid index
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        // Close suggestions dropdown
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  /**
   * Handles input focus event.
   * 
   * Shows suggestions dropdown if input has value and suggestions are available.
   */
  const handleFocus = () => {
    if (value.length > 0 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  /**
   * Handles input blur event.
   * 
   * Hides suggestions dropdown after a short delay to allow clicking on suggestions.
   * Uses 150ms delay to ensure click events on suggestions are processed before hiding.
   * 
   * @param e - Blur event from input element
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      // Only hide if focus didn't move to suggestions dropdown
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    }, 150);
  };

  /**
   * Cleanup debounce timeout on component unmount.
   * 
   * Prevents memory leaks by clearing any pending timeouts when component is destroyed.
   */
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Hide suggestions when value changes externally (e.g., cleared programmatically).
   * 
   * Resets suggestions state when input value is cleared to ensure UI consistency.
   */
  useEffect(() => {
    if (value === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  }, [value]);

  return (
    <div className="autocomplete-container">
      {/* Input field with autocomplete functionality */}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`emp-name-input ${className} ${disabled ? 'disabled-input' : ''}`}
        autoComplete="off"
      />
      
      {/* Suggestions dropdown - only shown when showSuggestions is true */}
      {showSuggestions && (
        <div 
          ref={suggestionsRef}
          className="suggestions-dropdown"
        >
          {/* Loading state */}
          {isLoading ? (
            <div className="suggestion-item loading">
              <span>検索中...</span>
            </div>
          ) : suggestions.length > 0 ? (
            /**
             * Render suggestion list.
             * Highlights selected item and updates selection on mouse hover.
             */
            suggestions.map((suggestion, index) => (
              <div
                key={suggestion}
                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <span>{suggestion}</span>
              </div>
            ))
          ) : (
            /* No results message */
            <div className="suggestion-item no-results">
              <span>該当する従業員が見つかりません</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
